import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { formatUSD, daysBetween, formatDate } from "../lib/format";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Package, Clock, CheckCircle, Calendar } from "@phosphor-icons/react";

export default function Purchasing() {
  const { session } = useAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const isProtected = session?.hotel?.license_status === "PROTEGIDA";

  const load = () => {
    api.get("/reports").then(r => setReports(r.data));
    api.get("/suppliers").then(r => setSuppliers(r.data));
  };
  useEffect(() => { load(); }, []);

  const supplierName = (id) => suppliers.find(s => s.id === id)?.name || "Sin proveedor";

  const inbox = reports.filter(r => r.status === "ESPERANDO_COMPRA");
  const waiting = reports.filter(r => r.status === "ESPERANDO_REPUESTO");

  const confirm = async (id) => {
    try {
      const { data } = await api.post(`/reports/${id}/purchase-confirm`);
      toast.success(`Compra confirmada · ETA ${formatDate(data.eta)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const etaFor = (r) => {
    if (r.eta) return formatDate(r.eta);
    // Default ETA: purchase_confirmed_at + 5 días (mientras backend no lo devuelva)
    if (r.purchase_confirmed_at) {
      const d = new Date(r.purchase_confirmed_at);
      d.setDate(d.getDate() + 5);
      return formatDate(d.toISOString());
    }
    return "—";
  };

  const daysSince = (iso) => daysBetween(iso);

  return (
    <AppShell title="Compras">
      {isProtected && <div className="q-card p-3 mb-4 text-sm text-[color:var(--q-muted)]">Solo lectura · licencia PROTEGIDA</div>}

      {/* Inbox */}
      <section className="mb-8">
        <div className="q-label mb-3">Requisiciones aprobadas ({inbox.length})</div>
        <div className="grid gap-3">
          {inbox.length === 0 && <div className="q-card p-6 text-center text-[color:var(--q-muted)]">Bandeja vacía.</div>}
          {inbox.map(r => (
            <div key={r.id} data-testid={`requisicion-${r.room}`} onClick={() => nav(`/report/${r.id}`)} className="q-card p-4 cursor-pointer">
              <div className="flex flex-wrap sm:flex-nowrap items-start gap-3 sm:gap-4">
                <div className="text-2xl font-mono font-light break-words min-w-0 basis-full sm:basis-auto sm:min-w-[3rem] sm:max-w-[12rem] text-[color:var(--q-navy)]">{r.room}</div>
                <div className="flex-1 min-w-0 basis-full sm:basis-auto order-3 sm:order-2">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`chip chip-${r.severity} shrink-0`}>{r.severity.toUpperCase()}</span>
                    <span className="badge-route badge-repuesto shrink-0">Repuesto</span>
                  </div>
                  <div className="text-sm break-words">{r.note}</div>
                  <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="q-label text-[color:var(--q-muted)]">Proveedor</div>
                      <div className="font-semibold truncate">{supplierName(r.supplier_id)}</div>
                    </div>
                    <div>
                      <div className="q-label text-[color:var(--q-muted)]">Precio</div>
                      <div className="font-mono font-semibold">{formatUSD(r.estimated_cost || 0)}</div>
                    </div>
                  </div>
                </div>
                <button data-testid={`confirm-buy-${r.room}`} disabled={isProtected}
                        onClick={(e) => { e.stopPropagation(); confirm(r.id); }}
                        className="q-btn-gold text-sm disabled:opacity-40 shrink-0 ml-auto sm:ml-0 order-2 sm:order-3">
                  <Package size={14} className="inline mr-1"/> Confirmar compra
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Waiting parts */}
      <section>
        <div className="q-label mb-3">Esperando repuesto ({waiting.length})</div>
        <div className="grid gap-3">
          {waiting.map(r => (
            <div key={r.id} data-testid={`waiting-${r.room}`} onClick={() => nav(`/report/${r.id}`)}
                 className="q-card p-4 flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-3 sm:gap-4 cursor-pointer" style={{borderLeft: "4px solid #c9a227"}}>
              <div className="text-2xl font-mono font-light break-words min-w-0 basis-full sm:basis-auto sm:min-w-[3rem] sm:max-w-[12rem]">{r.room}</div>
              <div className="flex-1 min-w-0 basis-full sm:basis-auto order-3 sm:order-2">
                <div className="text-sm break-words">{r.note}</div>
                <div className="text-xs text-[color:var(--q-muted)] mt-1 flex items-center gap-3 flex-wrap">
                  <span className="truncate">Proveedor: {supplierName(r.supplier_id)}</span>
                  <span className="flex items-center gap-1 whitespace-nowrap" data-testid={`eta-${r.room}`}><Calendar size={11}/> ETA: {etaFor(r)}</span>
                </div>
              </div>
              <div className="text-right shrink-0 ml-auto sm:ml-0 order-2 sm:order-3">
                <div className="q-label text-[color:var(--q-muted)] flex items-center gap-1"><Clock size={11}/> Días</div>
                <div className="text-3xl font-mono font-light text-[color:var(--q-gold-dark)] q-pulse" data-testid={`days-${r.room}`}>
                  {daysSince(r.purchase_confirmed_at)}
                </div>
              </div>
            </div>
          ))}
          {waiting.length === 0 && <div className="q-card p-6 text-center text-[color:var(--q-muted)]">Sin repuestos en camino.</div>}
        </div>
      </section>
    </AppShell>
  );
}
